import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, ChevronDown, ChevronRight, Plus, Sparkles, Trash2 } from 'lucide-react';

import { GenerationLoadingOverlay } from '../../../components/ui/GenerationLoadingOverlay';
import { generateSessionDetailWithAi, generateUnitPlanWithAi, mapGenerateSessionDetailError, mapGenerateUnitPlanError } from '../../api/planApi';
import { HitoCard } from '../planning/HitoCard';
import {
  UnitPlanWizardDialog,
  type UnitPlanWizardPayload,
} from '../planning/UnitPlanWizardDialog';
import {
  applyAiUnitToPlan,
} from '../../constants/coursePlanPresets';
import type { CourseProfile } from '../../types/courseProfile';
import type { CoursePlan, PlanSession, PlanUnit } from '../../types/coursePlan';
import { resolveSessionConfig } from '../../lib/hitoArchetypeUi';
import {
  createEmptyPlanSession,
  createEmptyPlanUnit,
  getCoursePlan,
  saveCoursePlan,
} from '../../services/coursePlanStorage';

interface CoursePlanningPanelProps {
  course: CourseProfile;
  /** workspace = lista + detalle (mismo layout que materiales). */
  layout?: 'standalone' | 'workspace';
  selectedUnitId?: string | null;
  onSelectUnit?: (unitId: string | null) => void;
}

const UNIT_GENERATION_MESSAGES = [
  'Leyendo el perfil del curso…',
  'Diseñando la ruta de aprendizaje…',
  'Secuenciando hitos cognitivos…',
  'Definiendo evidencias de avance…',
] as const;

const SESSION_DETAIL_MESSAGES = [
  'Aplicando arquetipo de sesión…',
  'Diseñando fases propias del hito…',
  'Afinando roles docente y estudiante…',
] as const;

function sessionBrief(session: PlanSession): string {
  const notes = session.notes?.trim() ?? '';
  if (notes.length >= 8) return notes;
  const title = session.title.trim();
  if (title.length >= 8) return title;
  return notes || title;
}

function sessionDetailKey(unitId: string, sessionId: string): string {
  return `${unitId}:${sessionId}`;
}

function sortUnits(units: PlanUnit[]): PlanUnit[] {
  return [...units].sort((a, b) => a.order - b.order);
}

function sortSessions(sessions: PlanSession[]): PlanSession[] {
  return [...sessions].sort((a, b) => a.order - b.order);
}

export function CoursePlanningPanel({
  course,
  layout = 'standalone',
  selectedUnitId = null,
  onSelectUnit,
}: CoursePlanningPanelProps) {
  const [plan, setPlan] = useState<CoursePlan>(() => getCoursePlan(course.id));
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(() => new Set());
  const [aiOpen, setAiOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiStatus, setAiStatus] = useState<string | null>(null);
  const [generatingSessionKey, setGeneratingSessionKey] = useState<string | null>(null);
  const [sessionDetailStatus, setSessionDetailStatus] = useState<string | null>(null);
  const [expandedHitoKey, setExpandedHitoKey] = useState<string | null>(null);

  useEffect(() => {
    setPlan(getCoursePlan(course.id));
    setExpandedUnits(new Set());
    setExpandedHitoKey(null);
  }, [course.id]);

  const persist = useCallback(
    (next: CoursePlan) => {
      const saved = saveCoursePlan({ ...next, courseId: course.id });
      setPlan(saved);
      return saved;
    },
    [course.id],
  );

  const patchUnits = (units: PlanUnit[]) => {
    persist({ ...plan, units: sortUnits(units) });
  };

  const handleAddUnit = () => {
    setAiError(null);
    setAiStatus(null);
    setAiOpen(true);
  };

  const handleGenerateUnit = async (payload: UnitPlanWizardPayload) => {
    const trimmedBrief = payload.brief.trim();
    if (trimmedBrief.length < 8) {
      setAiError('Escribe un brief más detallado para la unidad (mínimo 8 caracteres).');
      return;
    }

    setIsGenerating(true);
    setShowOverlay(true);
    setAiError(null);

    try {
      const { unit: aiUnit, provider } = await generateUnitPlanWithAi({
        brief: trimmedBrief,
        title: payload.title,
        sessionCount: payload.sessionCount,
        courseProfile: course,
        generationMode: payload.generationMode,
        curriculumTarget: payload.curriculumTarget,
      });

      const generatedUnit = applyAiUnitToPlan(plan, aiUnit, {
        generationMode: payload.generationMode,
        curriculumTarget: payload.curriculumTarget ?? undefined,
      });
      const saved = persist({
        ...plan,
        units: sortUnits([...plan.units, generatedUnit]),
      });

      setExpandedUnits((current) => new Set(current).add(generatedUnit.id));
      setAiOpen(false);
      setAiStatus(
        provider === 'scaffold'
          ? 'Unidad generada en modo local (Mistral no configurado).'
          : `Ruta "${generatedUnit.title}" generada con ${generatedUnit.sessions.length} hitos.`,
      );
      setPlan(saved);
    } catch (error) {
      const code = error instanceof Error ? error.message : 'generate_unit_plan_failed';
      setAiError(mapGenerateUnitPlanError(code));
    } finally {
      setIsGenerating(false);
      setShowOverlay(false);
    }
  };

  const handleUpdateUnit = (unitId: string, patch: Partial<PlanUnit>) => {
    patchUnits(
      plan.units.map((unit) => (unit.id === unitId ? { ...unit, ...patch } : unit)),
    );
  };

  const handleDeleteUnit = (unitId: string, title: string) => {
    if (!window.confirm(`¿Eliminar la unidad "${title || 'sin título'}"?`)) return;
    patchUnits(plan.units.filter((unit) => unit.id !== unitId));
  };

  const handleAddSession = (unitId: string) => {
    const unit = plan.units.find((item) => item.id === unitId);
    if (!unit) return;
    const session = createEmptyPlanSession(unit.sessions.length);
    handleUpdateUnit(unitId, {
      sessions: sortSessions([...unit.sessions, session]),
    });
  };

  const handleUpdateSession = (
    unitId: string,
    sessionId: string,
    patch: Partial<PlanSession>,
  ) => {
    const unit = plan.units.find((item) => item.id === unitId);
    if (!unit) return;
    handleUpdateUnit(unitId, {
      sessions: sortSessions(
        unit.sessions.map((session) =>
          session.id === sessionId ? { ...session, ...patch } : session,
        ),
      ),
    });
  };

  const handleDeleteSession = (unitId: string, sessionId: string, title: string) => {
    if (!window.confirm(`¿Eliminar la sesión "${title || 'sin título'}"?`)) return;
    const unit = plan.units.find((item) => item.id === unitId);
    if (!unit) return;
    handleUpdateUnit(unitId, {
      sessions: unit.sessions.filter((session) => session.id !== sessionId),
    });
  };

  const handleGenerateSessionDetail = async (
    unit: PlanUnit,
    session: PlanSession,
    sessionIndex: number,
  ): Promise<{ ok: boolean; error?: string }> => {
    const brief = sessionBrief(session);
    if (brief.length < 8) {
      return {
        ok: false,
        error: 'Escribe un foco más detallado en el hito antes de generar.',
      };
    }

    const config = session.config ?? resolveSessionConfig(session);
    const key = sessionDetailKey(unit.id, session.id);
    setGeneratingSessionKey(key);
    setShowOverlay(true);
    setSessionDetailStatus(null);

    try {
      const { sessionDetail, provider } = await generateSessionDetailWithAi({
        brief,
        sessionTitle: session.title,
        unitTitle: unit.title,
        unitObjective: unit.objective,
        sessionConfig: config,
        sessionIndex: sessionIndex + 1,
        totalSessions: unit.sessions.length,
        curriculumSlice: session.curriculumSlice,
        courseProfile: course,
        generationMode: unit.generationMode ?? 'curricular',
        curriculumTarget: unit.curriculumTarget ?? null,
      });

      handleUpdateSession(unit.id, session.id, {
        title: session.title,
        notes: session.notes,
        plannedDate: session.plannedDate,
        config,
        detail: sessionDetail.detail,
      });

      setSessionDetailStatus(
        provider === 'scaffold'
          ? 'Detalle generado en modo local (Mistral no configurado).'
          : 'Detalle micro generado con IA.',
      );
      return { ok: true };
    } catch (error) {
      const code = error instanceof Error ? error.message : 'generate_session_detail_failed';
      const message = mapGenerateSessionDetailError(code);
      setSessionDetailStatus(message);
      return { ok: false, error: message };
    } finally {
      setGeneratingSessionKey(null);
      setShowOverlay(false);
    }
  };

  const toggleHito = (unitId: string, sessionId: string) => {
    const key = sessionDetailKey(unitId, sessionId);
    setExpandedHitoKey((current) => (current === key ? null : key));
  };

  const expandHito = (unitId: string, sessionId: string) => {
    setExpandedHitoKey(sessionDetailKey(unitId, sessionId));
  };

  const toggleUnit = (unitId: string) => {
    setExpandedUnits((current) => {
      const next = new Set(current);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  };

  const units = sortUnits(plan.units);
  const isWorkspace = layout === 'workspace';
  const activeUnitId = isWorkspace
    ? selectedUnitId && units.some((u) => u.id === selectedUnitId)
      ? selectedUnitId
      : units[0]?.id ?? null
    : null;
  const activeUnit = activeUnitId ? units.find((u) => u.id === activeUnitId) ?? null : null;
  const activeUnitIndex = activeUnit ? units.findIndex((u) => u.id === activeUnit.id) : -1;

  useEffect(() => {
    if (!isWorkspace || !activeUnitId) return;
    if (selectedUnitId !== activeUnitId) onSelectUnit?.(activeUnitId);
  }, [isWorkspace, activeUnitId, selectedUnitId, onSelectUnit]);

  const addEmptyUnit = () => {
    const unit = createEmptyPlanUnit(plan.units.length);
    patchUnits([...plan.units, unit]);
    setExpandedUnits((current) => new Set(current).add(unit.id));
    onSelectUnit?.(unit.id);
  };

  const renderUnitDetail = (unit: PlanUnit, unitIndex: number, options?: { showHeader?: boolean }) => {
    const showHeader = options?.showHeader !== false;
    const sessions = sortSessions(unit.sessions);
    return (
      <div className="space-y-4">
        {showHeader ? (
          <div className="flex flex-wrap items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--sala-ink-muted)]">
                Unidad {unitIndex + 1}
              </p>
              <input
                className="sala-input-dark mt-1 w-full text-sm font-semibold shadow-sm"
                value={unit.title}
                onChange={(event) => handleUpdateUnit(unit.id, { title: event.target.value })}
                placeholder="Título de la unidad"
              />
            </div>
            <button
              type="button"
              className="sala-btn-outline shrink-0 py-1.5 text-[10px] text-red-400"
              onClick={() => handleDeleteUnit(unit.id, unit.title)}
              aria-label="Eliminar unidad"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}

        <textarea
          className="sala-input-dark min-h-[72px] w-full resize-y text-xs shadow-sm"
          value={unit.objective ?? ''}
          onChange={(event) => handleUpdateUnit(unit.id, { objective: event.target.value })}
          placeholder="Objetivo o foco de la unidad"
        />

        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-wide text-[var(--sala-ink-muted)]">
            Hitos ({sessions.length})
          </p>
          <button
            type="button"
            className="sala-btn-outline py-1 text-[10px] shadow-sm"
            onClick={() => handleAddSession(unit.id)}
          >
            <Plus className="mr-1 inline h-3 w-3" />
            Sesión
          </button>
        </div>

        {sessions.length === 0 ? (
          <p className="text-xs text-[var(--sala-ink-muted)]">
            Añade sesiones para planificar clases dentro de esta unidad.
          </p>
        ) : (
          <ul className="relative ml-1 border-l border-gray-200 pl-4">
            {sessions.map((session, sessionIndex) => (
              <HitoCard
                key={session.id}
                session={session}
                hitoIndex={sessionIndex}
                isLast={sessionIndex === sessions.length - 1}
                courseId={course.id}
                subjectId={course.subjectId}
                subjectTag={course.subjectTag}
                level={course.level}
                unit={unit}
                isExpanded={expandedHitoKey === sessionDetailKey(unit.id, session.id)}
                onToggleExpand={() => toggleHito(unit.id, session.id)}
                onAutoExpand={() => expandHito(unit.id, session.id)}
                isGenerating={generatingSessionKey === sessionDetailKey(unit.id, session.id)}
                onUpdate={(patch) => handleUpdateSession(unit.id, session.id, patch)}
                onDelete={() => handleDeleteSession(unit.id, session.id, session.title)}
                onGenerateDetail={(draftSession) =>
                  handleGenerateSessionDetail(unit, draftSession, sessionIndex)
                }
              />
            ))}
          </ul>
        )}
      </div>
    );
  };

  const statusMessages = (
    <>
      {sessionDetailStatus ? (
        <p className="text-sm text-[var(--sala-accent-neon)]">{sessionDetailStatus}</p>
      ) : null}
      {aiStatus ? <p className="text-sm text-[var(--sala-accent-neon)]">{aiStatus}</p> : null}
    </>
  );

  const ctaButtons = (
    <div className="flex flex-wrap gap-2">
      <button type="button" className="course-workspace__cta" onClick={handleAddUnit}>
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
        Añadir unidad
      </button>
      <button type="button" className="course-workspace__cta course-workspace__cta--ghost" onClick={addEmptyUnit}>
        <Plus className="h-3.5 w-3.5" aria-hidden />
        Vacía
      </button>
    </div>
  );

  if (isWorkspace) {
    return (
      <>
        <GenerationLoadingOverlay
          visible={showOverlay}
          messages={generatingSessionKey ? SESSION_DETAIL_MESSAGES : UNIT_GENERATION_MESSAGES}
        />

        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {statusMessages}
          {ctaButtons}
        </div>

        <section className="course-materials-workspace">
          <aside className="course-materials-workspace__list">
            {units.length === 0 ? (
              <div className="sala-empty-state py-10">
                <CalendarDays
                  className="mx-auto h-8 w-8 text-[var(--micelio-terracota,#e07a5f)]"
                  strokeWidth={1.75}
                />
                <p className="sala-empty-state__title mt-3">Sin unidades</p>
                <p className="sala-empty-state__hint">Generá una con IA o creá una vacía.</p>
              </div>
            ) : (
              <ul
                className="studio-resource-list studio-resource-list--picker space-y-2"
                role="listbox"
                aria-label="Unidades"
              >
                {units.map((unit, unitIndex) => {
                  const selected = unit.id === activeUnitId;
                  const sessions = sortSessions(unit.sessions);
                  return (
                    <li key={unit.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`studio-resource-row studio-soft-card studio-resource-row--selectable w-full text-left${
                          selected ? ' studio-resource-row--selected' : ''
                        }`}
                        onClick={() => onSelectUnit?.(unit.id)}
                      >
                        <span className="studio-resource-row__icon" aria-hidden>
                          <CalendarDays className="h-4 w-4" />
                        </span>
                        <span className="studio-resource-row__body min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-[var(--sala-ink)]">
                            {unit.title.trim() || `Unidad ${unitIndex + 1}`}
                          </p>
                          <p className="mt-0.5 truncate text-xs text-[var(--sala-ink-muted)]">
                            {sessions.length} hito{sessions.length === 1 ? '' : 's'}
                            {unit.objective?.trim()
                              ? ` · ${unit.objective.trim().slice(0, 48)}`
                              : ''}
                          </p>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          <div className="course-materials-workspace__preview">
            {activeUnit && activeUnitIndex >= 0 ? (
              <div className="course-material-preview">
                <header className="course-material-preview__chrome">
                  <div className="min-w-0 flex-1">
                    <p className="course-material-preview__eyebrow">Unidad</p>
                    <h2 className="course-material-preview__title">
                      {activeUnit.title.trim() || `Unidad ${activeUnitIndex + 1}`}
                    </h2>
                  </div>
                </header>
                <div className="course-material-preview__body p-4">
                  {renderUnitDetail(activeUnit, activeUnitIndex)}
                </div>
              </div>
            ) : (
              <div className="course-material-preview course-material-preview--empty">
                <p className="course-material-preview__empty-title">Planificación</p>
                <p className="course-material-preview__empty-hint">
                  Seleccioná una unidad o creá la primera.
                </p>
              </div>
            )}
          </div>
        </section>

        <UnitPlanWizardDialog
          open={aiOpen}
          onOpenChange={setAiOpen}
          course={course}
          isGenerating={isGenerating}
          error={aiError}
          onGenerate={handleGenerateUnit}
        />
      </>
    );
  }

  return (
    <>
      <GenerationLoadingOverlay
        visible={showOverlay}
        messages={generatingSessionKey ? SESSION_DETAIL_MESSAGES : UNIT_GENERATION_MESSAGES}
      />

      <div
        className="rounded-2xl border p-6 shadow-sm"
        style={{
          borderColor: 'var(--sala-border-strong)',
          background: 'var(--sala-surface-raised)',
        }}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-bold">Planificación del curso</h2>
            <p className="mt-2 max-w-lg text-sm text-[var(--sala-ink-muted)]">
              Organiza unidades y sesiones para <strong>{course.displayName}</strong>. Genera secuencias
              con IA o edítalas manualmente.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="sala-btn-hero inline-flex text-xs shadow-sm"
              onClick={handleAddUnit}
            >
              <Plus className="mr-1.5 inline h-3.5 w-3.5" />
              Añadir unidad
            </button>
            <button type="button" className="sala-btn-outline inline-flex text-xs shadow-sm" onClick={addEmptyUnit}>
              <Plus className="mr-1.5 inline h-3.5 w-3.5" />
              Crear vacía
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-2">{statusMessages}</div>

        {units.length === 0 ? (
          <div
            className="mt-8 rounded-xl border border-dashed p-8 text-center"
            style={{ borderColor: 'var(--sala-border-strong)' }}
          >
            <CalendarDays className="mx-auto h-8 w-8 text-[var(--sala-accent-neon)]" strokeWidth={1.75} />
            <p className="mt-3 font-display text-sm font-bold">Sin unidades planificadas</p>
            <p className="mt-1 text-xs text-[var(--sala-ink-muted)]">
              Genera una unidad con IA o créala manualmente para estructurar el año o semestre.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <button type="button" className="sala-btn-hero text-xs" onClick={handleAddUnit}>
                <Plus className="mr-1.5 inline h-3.5 w-3.5" />
                Añadir unidad
              </button>
              <button type="button" className="sala-btn-outline text-xs" onClick={addEmptyUnit}>
                Crear vacía
              </button>
            </div>
          </div>
        ) : (
          <ul className="mt-6 space-y-3">
            {units.map((unit, unitIndex) => {
              const expanded = expandedUnits.has(unit.id);
              const sessions = sortSessions(unit.sessions);

              return (
                <li
                  key={unit.id}
                  className="rounded-xl border shadow-sm"
                  style={{ borderColor: 'var(--sala-border-strong)' }}
                >
                  <div className="flex items-start gap-2 p-4">
                    <button
                      type="button"
                      className="mt-1 shrink-0 rounded p-1 text-[var(--sala-ink-muted)] hover:text-[var(--sala-accent-neon)]"
                      onClick={() => toggleUnit(unit.id)}
                      aria-expanded={expanded}
                      aria-label={expanded ? 'Contraer unidad' : 'Expandir unidad'}
                    >
                      {expanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>

                    <div className="min-w-0 flex-1 space-y-3">
                      <div className="flex flex-wrap items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--sala-ink-muted)]">
                            Unidad {unitIndex + 1}
                          </p>
                          <input
                            className="sala-input-dark mt-1 w-full text-sm font-semibold shadow-sm"
                            value={unit.title}
                            onChange={(event) =>
                              handleUpdateUnit(unit.id, { title: event.target.value })
                            }
                            placeholder="Título de la unidad"
                          />
                        </div>
                        <button
                          type="button"
                          className="sala-btn-outline shrink-0 py-1.5 text-[10px] text-red-400"
                          onClick={() => handleDeleteUnit(unit.id, unit.title)}
                          aria-label="Eliminar unidad"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {expanded ? (
                        <div
                          className="space-y-4 border-t pt-4"
                          style={{ borderColor: 'var(--sala-border-strong)' }}
                        >
                          {renderUnitDetail(unit, unitIndex, { showHeader: false })}
                        </div>
                      ) : (
                        <p className="text-xs text-[var(--sala-ink-muted)]">
                          {sessions.length > 0
                            ? `${sessions.length} sesión${sessions.length === 1 ? '' : 'es'}`
                            : 'Sin sesiones'}
                          {unit.objective?.trim() ? ` · ${unit.objective.trim().slice(0, 64)}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <UnitPlanWizardDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        course={course}
        isGenerating={isGenerating}
        error={aiError}
        onGenerate={handleGenerateUnit}
      />
    </>
  );
}
